import { BasicChartDataSource } from '@app/data/interface/widget-config';

/**
 * Builds a set of allowed source identifiers derived from the selected sources.
 * Any entry with identifier 'all' disables filtering so consumers can fall back
 * to their default expansion behaviour.
 *
 * @param selectedSources The selected data sources.
 * @returns A set of allowed normalized source identifiers, or null if no filtering is applied.
 */
export const buildAllowedIdentifierSet = (selectedSources?: BasicChartDataSource[]): Set<string> | null => {
  if (!Array.isArray(selectedSources) || selectedSources.length === 0) return null;

  const normalizedIdentifiers = selectedSources
    .map((source) =>
      typeof source?.environmentalDataSourceIdentifier === 'string'
        ? source.environmentalDataSourceIdentifier.trim().toLowerCase()
        : ''
    )
    .filter((identifier): identifier is string => identifier.length > 0);

  if (!normalizedIdentifiers.length) return null;
  if (normalizedIdentifiers.includes('all')) return null;

  return new Set(normalizedIdentifiers);
};

/**
 * Builds a mapping from environmental data source IDs to their human readable identifiers.
 * @param seriesData The series data containing environmental data sources.
 * @return A map of source IDs to their environmental data source identifiers.
 */
export const buildSourceIdToIdentifierMap = (seriesData: any): Map<string, string> => {
  const map = new Map<string, string>();
  const sources: any[] = Array.isArray(seriesData?.environmentalDataSources) ? seriesData.environmentalDataSources : [];

  sources.forEach((source: any) => {
    if (!source) return;
    const rawIdentifier = typeof source.source_identifier === 'string' ? source.source_identifier.trim() : '';
    if (!rawIdentifier) return;
    if (source.id != null) {
      map.set(String(source.id), rawIdentifier);
    }
  });

  return map;
};

/**
 * Resolves the normalised environmental data source identifier for a data point.
 * @param dataPoint The data point to resolve.
 * @param sourceIdToIdentifier The mapping of source IDs to their environmental data source identifiers.
 * @returns The normalised source identifier or null if not found.
 */
export const resolveNormalizedSourceIdentifier = (
  dataPoint: any,
  sourceIdToIdentifier: Map<string, string>
): string | null => {
  if (!dataPoint || typeof dataPoint !== 'object') return null;

  const rawSourceId =
    dataPoint.environmental_data_source_id ??
    dataPoint.environmentalDataSourceId ??
    dataPoint.source_id ??
    dataPoint.environmentalDataSourceIdentifier;

  if (rawSourceId == null) return null;

  const mapped = sourceIdToIdentifier.get(String(rawSourceId));
  if (typeof mapped === 'string' && mapped.trim().length > 0) {
    return mapped.trim().toLowerCase();
  }

  if (typeof rawSourceId === 'string' && rawSourceId.trim().length > 0) {
    return rawSourceId.trim().toLowerCase();
  }

  return null;
};
